<template>
  <div v-if="visible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="editUserModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editUserModalLabel">
            <i class="mdi mdi-account-edit me-2"></i>Edit User Details
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>

          <div v-else>
            <!-- Personal Information Section -->
            <h6 class="text-primary mb-3 text-uppercase">
              <i class="mdi mdi-account me-1"></i> Personal Information
            </h6>
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="fullName" class="form-label">Full Name <span class="text-danger">*</span></label>
                  <input
                      v-model="formData.full_name"
                      type="text"
                      class="form-control"
                      id="fullName"
                      placeholder="Enter full name"
                      required
                  >
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="username" class="form-label">Username</label>
                  <input
                      v-model="formData.username"
                      type="text"
                      class="form-control"
                      id="username"
                      placeholder="Username"
                      readonly
                  >
                  <small class="text-muted">Username cannot be changed</small>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="email" class="form-label">Email Address <span class="text-danger">*</span></label>
                  <input
                      v-model="formData.email"
                      type="email"
                      class="form-control"
                      id="email"
                      placeholder="email@example.com"
                      required
                  >
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="phone" class="form-label">Phone Number <span class="text-danger">*</span></label>
                  <input
                      v-model="formData.phone"
                      type="text"
                      class="form-control"
                      id="phone"
                      placeholder="Enter phone number"
                      required
                  >
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="gender" class="form-label">Gender</label>
                  <select class="form-select" v-model="formData.gender" id="gender">
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="nationalId" class="form-label">National ID</label>
                  <input
                      v-model="formData.national_id"
                      type="text"
                      class="form-control"
                      id="nationalId"
                      placeholder="Enter National ID"
                  >
                </div>
              </div>
            </div>

            <hr class="my-4">

            <!-- Employment Information Section -->
            <h6 class="text-primary mb-3 text-uppercase">
              <i class="mdi mdi-briefcase me-1"></i> Employment Information
            </h6>
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="employeeId" class="form-label">Employee ID</label>
                  <input
                      v-model="formData.employee_id"
                      type="text"
                      class="form-control"
                      id="employeeId"
                      placeholder="Enter Employee ID"
                  >
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label for="department" class="form-label">Department <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.department_id" id="department">
                    <option value="">Select Department</option>
                    <option
                        v-for="dept in departments"
                        :key="dept.department_id"
                        :value="dept.department_id"
                    >
                      {{ dept.department_name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Current Assignment Info (Read Only) -->
            <div v-if="user && (user.region_name || user.zone_name || user.woreda_name)" class="mt-3">
              <h6 class="text-muted mb-3">
                <i class="mdi mdi-map-marker me-1"></i> Current Assignment
              </h6>
              <div class="bg-light p-3 rounded">
                <div class="row">
                  <div class="col-md-4" v-if="user.region_name">
                    <small class="text-muted d-block">Region</small>
                    <span>{{ user.region_name }}</span>
                  </div>
                  <div class="col-md-4" v-if="user.zone_name">
                    <small class="text-muted d-block">Zone</small>
                    <span>{{ user.zone_name }}</span>
                  </div>
                  <div class="col-md-4" v-if="user.woreda_name">
                    <small class="text-muted d-block">Woreda</small>
                    <span>{{ user.woreda_name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="mdi mdi-close me-1"></i> Cancel
          </button>
          <button
              @click="handleSave"
              class="btn btn-primary"
              :disabled="saving"
          >
            <span v-if="saving">
              <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Saving...
            </span>
            <span v-else>
              <i class="mdi mdi-content-save me-1"></i> Save Changes
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="visible" class="modal-backdrop fade show"></div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "EditUserModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: null
    },
    roles: {
      type: Array,
      default: () => []
    },
    departments: {
      type: Array,
      default: () => []
    },
    hospitals: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'update-success'],
  data() {
    return {
      loading: false,
      saving: false,
      formData: {
        user_id: null,
        full_name: '',
        username: '',
        email: '',
        phone: '',
        gender: '',
        national_id: '',
        employee_id: '',
        department_id: '',
        category: '',
        hospital_id: '',
        title: '',
        date_of_birth: '',
        affiliation_id: '',
        assigned_region_id: '',
        assigned_zone_id: '',
        assigned_woreda_id: '',
        is_active: '1',
        must_change_password: false
      }
    }
  },
  watch: {
    user: {
      handler(newUser) {
        if (newUser && Object.keys(newUser).length > 0) {
          this.populateForm(newUser);
        }
      },
      immediate: true,
      deep: true
    },
    visible: {
      handler(newVal) {
        if (newVal && this.user) {
          this.populateForm(this.user);
        }
      }
    }
  },
  methods: {
    populateForm(userData) {
      console.log('Populating form with user data:', userData);

      // Parse employment details if it's a JSON string
      let employmentDetails = {};
      if (userData.employment_details) {
        try {
          employmentDetails = typeof userData.employment_details === 'string'
              ? JSON.parse(userData.employment_details)
              : userData.employment_details;
        } catch (e) {
          console.error('Error parsing employment details:', e);
        }
      }

      this.formData = {
        user_id: userData.user_id || null,
        full_name: userData.full_name || '',
        username: userData.username || '',
        email: userData.email || '',
        phone: userData.phone || '',
        gender: userData.gender || employmentDetails.gender || '',
        national_id: userData.national_id || employmentDetails.national_id || '',
        employee_id: userData.employee_id || '',
        department_id: userData.department_id || '',
        category: userData.category || '',
        hospital_id: userData.hospital_id || '',
        title: userData.title || employmentDetails.title || '',
        date_of_birth: userData.date_of_birth || employmentDetails.date_of_birth || '',
        affiliation_id: userData.affiliation_id || employmentDetails.affiliation_id || '',
        assigned_region_id: userData.assigned_region_id || '',
        assigned_zone_id: userData.assigned_zone_id || '',
        assigned_woreda_id: userData.assigned_woreda_id || '',
        is_active: userData.is_active ? String(userData.is_active) : '1',
        must_change_password: userData.must_change_password === '1' || userData.must_change_password === true
      };

      console.log('Form data populated:', this.formData);
    },

    validateForm() {
      const errors = [];

      if (!this.formData.full_name?.trim()) {
        errors.push('Full name is required');
      }

      if (!this.formData.phone?.trim()) {
        errors.push('Phone number is required');
      }

      if (!this.formData.department_id) {
        errors.push('Department is required');
      }

      return errors;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    async handleSave() {
      // Validate form
      const errors = this.validateForm();
      if (errors.length > 0) {
        alert('Please fix the following errors:\n\n' + errors.join('\n'));
        return;
      }

      this.saving = true;

      try {
        // Use snake_case field names to match PHP backend's $allowedFields
        const requestData = {
          function: "updateUser",
          userId: this.formData.user_id,
          // Fields matching PHP $allowedFields array (snake_case)
          full_name: this.formData.full_name,
          phone: this.formData.phone,
          gender: this.formData.gender,
          national_id: this.formData.national_id,
          employee_id: this.formData.employee_id,
          department_id: this.formData.department_id,
          category: this.formData.category,
          title: this.formData.title || null,
          date_of_birth: this.formData.date_of_birth || null,
          affiliation_id: this.formData.affiliation_id || null,
          // These fields use camelCase as per PHP code structure
          isActive: this.formData.is_active === '1' || this.formData.is_active === 1,
          mustChangePassword: this.formData.must_change_password ? '1' : '0'
        };

        console.log('Sending update request:', requestData);

        const response = await executeJson(requestData, authUrl);

        if (response.data.success) {
          this.$emit('update-success', {
            message: response.data.message || 'User updated successfully',
            userId: this.formData.user_id
          });
          this.$emit('close');
        } else {
          alert(response.data.message || 'Failed to update user');
        }
      } catch (error) {
        console.error('Error updating user:', error);
        alert(error.message || 'An error occurred while updating user');
      } finally {
        this.saving = false;
      }
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.modal-backdrop {
  z-index: 1040;
}

.modal {
  z-index: 1050;
}

.modal-lg {
  max-width: 800px;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.text-primary {
  color: #556ee6 !important;
}

hr {
  border-color: #e3e6f0;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}

.btn-primary {
  background-color: #556ee6;
  border-color: #556ee6;
}

.btn-primary:hover {
  background-color: #4458cb;
  border-color: #4458cb;
}

.btn-primary:disabled {
  background-color: #556ee6;
  border-color: #556ee6;
  opacity: 0.65;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>